// The Effect edge of the OPIN applications screen: the one module that runs its calls. Each run
// hands the screen a Promise of the call's Result, so a refusal is data the screen renders (and
// React Query never retries a 403 as if it were a transient throw); a defect rejects the Promise.
// A write answer is handed to the caller only, never to a React Query cache: the secret it may
// carry lives in the dialog that shows it and goes away with it.
import { queryOptions } from '@tanstack/react-query';
import { Crypto, Effect, Layer, ManagedRuntime, Schema } from 'effect';
import type { Result } from 'effect';
import { FetchHttpClient } from 'effect/http';
import { dual } from 'effect/Function';
import {
  OpinApplicationCreateBody,
  OpinApplicationUpdateBody,
} from '@finnest/power-admin-contracts';
import type {
  OpinApplicationIdempotencyKey,
  OpinApplicationList,
  OpinApplicationSecretIssued,
  OpinApplicationView,
} from '@finnest/power-admin-contracts';
import { resolvePowerAdminApiBaseUrl } from '../../runtime/power-admin-api.ts';
import {
  createApplication,
  disableApplication,
  enableApplication,
  listApplications,
  readSessionScopes,
  rotateApplicationSecret,
  updateApplication,
} from './applications.ts';
import type { ApplicationsOperation, ApplicationsRequestFailed } from './applications.ts';

// The browser's secure random bytes; the screen asks for a UUID only, so no digest is offered.
const BrowserCrypto = Layer.succeed(
  Crypto.Crypto,
  Crypto.make({
    randomBytes: (size) => globalThis.crypto.getRandomValues(new Uint8Array(size)),
    digest: () => Effect.die(new Error('The applications screen computes no digest.')),
  }),
);

const applicationsRuntime = ManagedRuntime.make(Layer.merge(FetchHttpClient.layer, BrowserCrypto));

/** A fresh key for one create or rotation; its retries reuse it, a new submission gets another. */
export const newIdempotencyKey = (): OpinApplicationIdempotencyKey =>
  applicationsRuntime.runSync(
    Effect.gen(function* () {
      const crypto = yield* Crypto.Crypto;
      return yield* crypto.randomUUIDv4();
    }),
  );

/** What an applications call answered: its value or why it failed. */
export type ApplicationsOutcome<A> = Result.Result<A, ApplicationsRequestFailed>;

// The fetch is read per run, as the browser (or a test) has it now; the API base is resolved per
// run too.
const runCall = <A>(
  operation: ApplicationsOperation<A>,
  signal: AbortSignal,
): Promise<ApplicationsOutcome<A>> =>
  applicationsRuntime.runPromise(
    Effect.suspend(() => operation(resolvePowerAdminApiBaseUrl())).pipe(
      Effect.result,
      Effect.provideService(FetchHttpClient.Fetch, globalThis.fetch),
    ),
    { signal },
  );

export const applicationsQueryOptions = queryOptions({
  queryKey: ['power-admin', 'opin-applications', 'list'] as const,
  queryFn: ({ signal }): Promise<ApplicationsOutcome<OpinApplicationList>> =>
    runCall(listApplications, signal),
  retry: false,
});

/** The session's scopes, read once per page load: what every Admin screen gates its actions on. */
export const sessionScopesQueryOptions = queryOptions({
  queryKey: ['power-admin', 'operator-session', 'scopes'] as const,
  queryFn: ({ signal }): Promise<ApplicationsOutcome<readonly string[]>> =>
    runCall(readSessionScopes, signal),
  staleTime: 'static',
  retry: false,
});

// A write is not cut off by the screen going away: its own deadline (in the client) ends it as an
// outcome the screen reports as uncertain, never as a failure. This signal never aborts.
const UNTIL_POWER_ANSWERS = new AbortController().signal;

/** Creates an application under `idempotencyKey`; the answer's secret is the caller's to show once. */
export const createApplicationOutcome: {
  (
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): (body: OpinApplicationCreateBody) => Promise<ApplicationsOutcome<OpinApplicationSecretIssued>>;
  (
    body: OpinApplicationCreateBody,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): Promise<ApplicationsOutcome<OpinApplicationSecretIssued>>;
} = dual(
  2,
  (
    body: OpinApplicationCreateBody,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): Promise<ApplicationsOutcome<OpinApplicationSecretIssued>> =>
    runCall(createApplication(body, idempotencyKey), UNTIL_POWER_ANSWERS),
);

/** Issues a new secret for `clientId` under `idempotencyKey`; the caller shows it once. */
export const rotateSecretOutcome: {
  (
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): (clientId: string) => Promise<ApplicationsOutcome<OpinApplicationSecretIssued>>;
  (
    clientId: string,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): Promise<ApplicationsOutcome<OpinApplicationSecretIssued>>;
} = dual(
  2,
  (
    clientId: string,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): Promise<ApplicationsOutcome<OpinApplicationSecretIssued>> =>
    runCall(rotateApplicationSecret(clientId, idempotencyKey), UNTIL_POWER_ANSWERS),
);

/** Renames `clientId` or changes its description. */
export const updateApplicationOutcome: {
  (
    body: OpinApplicationUpdateBody,
  ): (clientId: string) => Promise<ApplicationsOutcome<OpinApplicationView>>;
  (
    clientId: string,
    body: OpinApplicationUpdateBody,
  ): Promise<ApplicationsOutcome<OpinApplicationView>>;
} = dual(
  2,
  (
    clientId: string,
    body: OpinApplicationUpdateBody,
  ): Promise<ApplicationsOutcome<OpinApplicationView>> =>
    runCall(updateApplication(clientId, body), UNTIL_POWER_ANSWERS),
);

/** Enables `clientId`. */
export const enableOutcome = (
  clientId: string,
): Promise<ApplicationsOutcome<OpinApplicationView>> =>
  runCall(enableApplication(clientId), UNTIL_POWER_ANSWERS);

/** Disables `clientId`. */
export const disableOutcome = (
  clientId: string,
): Promise<ApplicationsOutcome<OpinApplicationView>> =>
  runCall(disableApplication(clientId), UNTIL_POWER_ANSWERS);

/** What the name and description fields hold. */
export type ApplicationFields = { readonly name: string; readonly description: string };

const CLOSED = { onExcessProperty: 'error' } as const;

/** The create body the fields hold: the name trimmed, an empty description left out. */
export const createBodyOf = (
  fields: ApplicationFields,
): Result.Result<OpinApplicationCreateBody, Schema.SchemaError> => {
  const name = fields.name.trim();
  return Schema.decodeResult(OpinApplicationCreateBody)(
    fields.description === '' ? { name } : { name, description: fields.description },
    CLOSED,
  );
};

/**
 * The edit body: only what the operator changed. A name left as it was (or left empty on an
 * application Keycloak holds without one) is not sent; an emptied description clears it.
 */
export const updateBodyOf: {
  (
    fields: ApplicationFields,
  ): (
    original: OpinApplicationView,
  ) => Result.Result<OpinApplicationUpdateBody, Schema.SchemaError>;
  (
    original: OpinApplicationView,
    fields: ApplicationFields,
  ): Result.Result<OpinApplicationUpdateBody, Schema.SchemaError>;
} = dual(
  2,
  (
    original: OpinApplicationView,
    fields: ApplicationFields,
  ): Result.Result<OpinApplicationUpdateBody, Schema.SchemaError> => {
    const decode = Schema.decodeResult(OpinApplicationUpdateBody);
    const name = fields.name.trim();
    const { description } = fields;
    const nameChanged = name !== (original.name ?? '');
    const descriptionChanged = description !== (original.description ?? '');
    if (nameChanged && descriptionChanged) return decode({ name, description }, CLOSED);
    if (nameChanged) return decode({ name }, CLOSED);
    // Nothing changed decodes to a refusal: the contract wants at least one field.
    return decode(descriptionChanged ? { description } : {}, CLOSED);
  },
);
